import React, { useEffect, useContext, useState, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { Modal } from "antd"
import moment from "moment";

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Tooltip from '@mui/material/Tooltip';
import Typography from "@mui/material/Typography";
import useMediaQuery from '@mui/material/useMediaQuery';

import ExitToAppIcon from "@mui/icons-material/ExitToApp";

import brand from '../contexts/brand';
import { DataContext } from 'contexts/DataContext';

import { doDownloadStudy, handleDownloadIamges } from 'helpers/handleDownloadIamges';
import handleDownloadStudy from 'helpers/handleDownloadStudy';
import handleOpenReport from 'helpers/handleOpenReport';
import handleOpenViewer from 'helpers/handleOpenViewer';
import handlePrintStudy from 'helpers/handlePrintStudy';

import LanguageSelector from 'components/LanguageSelector';
import ReportModal from "components/ReportModal";
import ViewerModal from "components/ViewerModal";

import { classes } from './styles/DashboardStyle';
import './styles/DashboardComplete.css';

import logo from 'images/human_logo.png';
import humanDr from 'images/human_dr.png';
import vetDr from 'images/vet_dr.png';
import humanLogoHeader from 'images/human_logo_header.png';
import vetLogoHeader from 'images/vet_logo_header.png';
import backgroundHuman from 'images/background_human.png';
import backgroundVet from 'images/background_vet.png';

const CHECK_DOWNLOAD_INTERVAL = 3000;

const DashboardComplete = () => {
  const isSmall = useMediaQuery('(max-width: 800px)');

  const navigate = useNavigate();

  const { dataList } = useContext(DataContext);

  const [downloadLaudoUrl, setDownloadLaudoUrl] = useState();
  const [laudoOpen, setLaudoOpen] = useState(false);
  const [loadingLaudo, setLoadingLaudo] = useState(false);
  const [urlLaudo, setUrlLaudo] = useState();
  const [viewerUrl, setViewerUrl] = useState(false);
  const { i18n, t } = useTranslation();
  const formatDate = i18n.language === 'en' || i18n.language === 'en-US' ? 'MM/DD/YYYY HH:mm:ss' : 'DD/MM/YYYY HH:mm:ss';

  const [downloadStatus, setDownloadStatus] = useState("none");
  const downloadZipUrl = useRef();

  const [printStatus, setPrintStatus] = useState("none");
  const printTotalTime = useRef();
  const printCanceled = useRef(false);
  const [printBuffer, setPrintBuffer] = useState();

  const hasData = dataList.length > 0;

  const accountId = dataList?.[0]?.study?.accountId;
  const canDownloadImages = dataList?.[0]?.downloadImagesLink;
  const canDownloadDicom = dataList?.[0]?.downloadStudyLink;

  useEffect(() => console.log(downloadLaudoUrl), [downloadLaudoUrl]);

  useEffect(() => {
    if (printCanceled.current === false && printBuffer) {
      // Abrindo o PDF
      try {
        const file = new Blob([printBuffer], { type: "application/pdf" });
        const printWindow = window.open(
          window.URL.createObjectURL(file),
          "_blank"
        );
        if (!printWindow) {
          throw new Error(t('Verifique o bloqueador de pop-ups do seu navegador'));
        }
      } catch (e) {
        window.alert(e.message);
      }
    };
    setPrintBuffer(null);
    setPrintStatus('none');
  }, [printBuffer, t]);

  if (!hasData) return <Navigate to="/" />

  return (
    <>
      <AppBar color="primary" position="static" sx={classes.appBar}>
        <img src={brand === 'vet' ? vetLogoHeader : humanLogoHeader} alt={brand === 'vet' ? 'Dr. Nuvem' : 'Dr. Tis'} style={{maxHeight: 30}} />

        <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto'}}>
          <LanguageSelector />
          <Button
            endIcon={<ExitToAppIcon />}
            onClick={() => navigate("/v2")}
            variant='contained'
            color='primary'
            sx={{ ml: 2 }}
          >
            {t('Sair')}
          </Button>
        </Box>
      </AppBar>

      <div
        style={{
          height: '100vh',
          backgroundImage: !isSmall && (brand === 'vet' ? `url(${backgroundVet}`: `url(${backgroundHuman})`),
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center center'
        }}
      >
        <Container sx={classes.completeContainer}>
          <Modal
            maskClosable={false}
            title={t('Baixar estudo')}
            visible={downloadStatus !== "none"}
            okButtonProps={{
              disabled: downloadStatus !== "done",
              loading: downloadStatus === "zipping",
            }}
            onOk={() => doDownloadStudy(downloadZipUrl)}
            okText={t('Baixar')}
            onCancel={() => {
              setDownloadStatus("none");
            }}
          >
            {downloadStatus === "zipping" && t('Gerando o zip das imagens')}
            {downloadStatus === "done" &&
              t('Pronto! Clique em "Baixar" para fazer o download')}
            {downloadStatus.startsWith('Erro') && downloadStatus}
          </Modal>

          <Modal
            maskClosable={false}
            title={t('Imprimir estudo')}
            visible={printStatus !== "none"}
            okButtonProps={{
              disabled: printStatus !== "done",
              loading: printStatus === "printing",
            }}
            onCancel={() => {
              setPrintStatus("none");
              printCanceled.current = true;
            }}
          >
            {printStatus === "printing" && t('Imprimindo o estudo')}
            {printStatus.startsWith('Erro') && printStatus}
          </Modal>

          <Paper sx={classes.paper}>
            <Grid sx={classes.infoInstitution}>
              {dataList[0].study?.accountLogo ? (
                <Box
                  component='img'
                  src={dataList[0].study?.accountLogo}
                  alt={dataList[0].study.accountName || 'Dr. TIS'}
                  sx={classes.logo}
                />
              ) : (
                <Box component='img' src={brand === 'vet' ? vetDr : humanDr} alt={brand === 'vet' ? 'Dr. Nuvem' : 'Dr. Tis'} sx={classes.logo} />
              )}
              {dataList[0].study?.accountName && (
                <Grid item>
                  <Typography variant="h3" as="span" display="block">
                    {dataList[0].study.accountName}
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Grid container sx={classes.informations}>
              {(dataList[0]?.study?.socialName || dataList[0]?.study?.patientName) && (
                <Grid item>
                  <Typography variant="h4">{t('Nome do paciente')}</Typography>
                  <Typography variant="h3">
                    {dataList[0].study?.isForHuman === false 
                      ? dataList[0].study.patientName 
                      : (dataList[0].study.socialName || dataList[0].study.patientName)}
                  </Typography>
                </Grid>
              )}
              {dataList[0]?.study?.patientSex && (
                <Grid item>
                  <Typography variant="h4">{t('Sexo')}</Typography>
                  <Typography variant="h3">
                    {dataList[0]?.study?.patientSex}
                  </Typography>
                </Grid>
              )}
              {dataList[0]?.study?.patientAge && (
                <Grid item>
                  <Typography variant="h4">{t('Idade')}</Typography>
                  <Typography variant="h3">
                    {dataList[0]?.study?.patientAge}
                  </Typography>
                </Grid>
              )}

              {dataList[0]?.study?.patientMother && (
                <Grid item>
                  <Typography variant="h4">{t('Nome da mãe')}</Typography>
                  <Typography variant="h3">
                    {dataList[0]?.study?.patientMother}
                  </Typography>
                </Grid>
              )}
              {dataList[0]?.study?.patientResponsiblePerson && (
                <Grid item>
                  <Typography variant="h4">{t('Responsável')}</Typography>
                  <Typography variant="h3">
                    {dataList[0]?.study?.patientResponsiblePerson}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          <Paper sx={classes.paper}>
              <table className='responsive-table'>
                <thead>
                  <tr>
                    <th>{t('Data exame')}</th>
                    <th>{t('Exame')}</th>
                    <th>{t('Modalidade')}</th>
                    <th>{t('Instituição')}</th>
                    <th>{t('Abrir laudo')}</th>
                    <th>{t('Ver imagens')}</th>
                    <th>{t('Imprimir imagens')}</th>
                    {canDownloadImages && (
                      <th>{t('Baixar imagens')}</th>
                    )}
                    {canDownloadDicom && (
                      <th>{t('Baixar DICOM')}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {dataList.map((data, studyIdx) => (
                    <tr key={data.study.id}>
                      <td>
                        <span className="cell-header">{t('Data exame')}:</span>
                        {moment(data.study.dicomCreatedAt).format(formatDate)}
                      </td>
                      <td>
                        <span className="cell-header">{t('Exame')}:</span>
                        {data.study.examType}
                      </td>
                      <td>
                        <span className="cell-header">{t('Modalidade')}:</span>
                        {data.study.modalities[0]}
                      </td>

                      <td>
                        <span className="cell-header">{t('Instituição')}:</span>
                        {data.study.institutionName}
                      </td>
                      <td>
                        <span className="cell-header">{t('Abrir laudo')}:</span>
                        <Button
                          variant='outlined'
                          onClick={() => handleOpenReport(
                            accountId, dataList[studyIdx].report.downloadUrl, data.study.patientName, setDownloadLaudoUrl,
                            setLaudoOpen, setLoadingLaudo, setUrlLaudo, data.study.id, { type: 'paciente' }
                          )}
                          disabled={!data?.report?.downloadUrl}
                        >
                          {t('Abrir laudo')}
                          {loadingLaudo && (
                            <CircularProgress
                              size={20}
                              style={{ marginLeft: 25, color: 'white' }}
                            />
                          )}
                        </Button>
                      </td>
                      <td>
                        <span className='cell-header'>{t('Ver imagens')}:</span>
                        <Tooltip title={
                          (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE')
                            ? t('Solicite à instituição a recuperação das imagens')
                            : data.study.storageClass === 'BEING_RESTORED'
                              ? t('Aguarde. Em até 12 horas o exame estará disponível')
                              : t('Ver imagens')
                        }>
                          <span style={{ margin: 'auto' }}>
                            <Button
                              variant='outlined'
                              onClick={() => handleOpenViewer(
                                accountId, data.study.patientName, setViewerUrl, data.study.id,
                                { type: 'paciente' }, data.viewerLink
                              )}
                              style={{
                                background: (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE')
                                  ? '#c30000'
                                  : data.study.storageClass === 'BEING_RESTORED' && '#ffff99',
                                border: '1px solid rgba(19, 11, 31, 0.5)',
                                color: (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE') && 'white'
                              }}
                              disabled={data.study.storageClass === 'GLACIER'
                                || data.study.storageClass === 'DEEP_ARCHIVE' || data.study.storageClass === 'BEING_RESTORED'}
                            >
                              {
                                (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE')
                                  ? t('Imagem arquivada')
                                  : data.study.storageClass === 'BEING_RESTORED'
                                    ? t('Imagem em recuperação')
                                    : t('Ver imagens')
                              }
                            </Button>
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        <span className="cell-header">{t('Imprimir imagens')}:</span>
                        <Tooltip title={
                          (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE')
                            ? t('Solicite à instituição a recuperação das imagens')
                            : data.study.storageClass === 'BEING_RESTORED'
                              ? t('Aguarde. Em até 12 horas o exame estará disponível')
                              : t('Imprimir imagens')
                        }>
                          <span style={{ margin: 'auto' }}>
                            <Button
                              variant="outlined"
                              onClick={() => handlePrintStudy(
                                accountId, data.study.patientName, printCanceled, data.printStudyLink,
                                printTotalTime, setPrintBuffer, setPrintStatus, data.study?.id, { type: 'paciente' }
                              )}
                              style={{
                                background: (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE')
                                  ? '#c30000'
                                  : data.study.storageClass === 'BEING_RESTORED' && '#ffff99',
                                border: '1px solid rgba(19, 11, 31, 0.5)',
                                color: (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE') && 'white'
                              }}
                              disabled={!data?.printImagesLink
                                || (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE'
                                  || data.study.storageClass === 'BEING_RESTORED')}
                            >
                              {
                                (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE')
                                  ? t('Imagem arquivada')
                                  : data.study.storageClass === 'BEING_RESTORED'
                                    ? t('Imagem em recuperação')
                                    : t('Imprimir imagens')
                              }
                            </Button>
                          </span>
                        </Tooltip>
                      </td>
                      {canDownloadImages && (
                        <td>
                          <span className="cell-header">{t('Baixar imagens')}:</span>
                          <Tooltip title={
                            (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE')
                              ? t('Solicite à instituição a recuperação das imagens')
                              : data.study.storageClass === 'BEING_RESTORED'
                                ? t('Aguarde. Em até 12 horas o exame estará disponível')
                                : t('Baixar imagens')
                          }>
                            <span style={{ margin: 'auto' }}>
                              <Button
                                variant='outlined'
                                onClick={() => handleDownloadIamges(
                                  accountId, data?.checkDownloadLink, CHECK_DOWNLOAD_INTERVAL, data?.downloadImagesLink,
                                  downloadZipUrl, data.study.patientName, setDownloadStatus, data.study.id, { type: 'paciente' }
                                )}
                                style={{
                                  background: (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE')
                                    ? '#c30000'
                                    : data.study.storageClass === 'BEING_RESTORED' && '#ffff99',
                                  border: '1px solid rgba(19, 11, 31, 0.5)',
                                  color: (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE') && 'white'
                                }}
                                disabled={!data?.downloadImagesLink
                                  || (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE'
                                    || data.study.storageClass === 'BEING_RESTORED')}
                              >
                                {
                                  (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE')
                                    ? t('Imagem arquivada')
                                    : data.study.storageClass === 'BEING_RESTORED'
                                      ? t('Imagem em recuperação')
                                      : t('Baixar imagens')
                                }
                              </Button>
                            </span>
                          </Tooltip>
                        </td>
                      )}
                      {canDownloadDicom && (
                        <td>
                          <span className="cell-header">{t('Baixar DICOM')}:</span>
                          <Tooltip title={
                            (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE')
                              ? t('Solicite à instituição a recuperação das imagens')
                              : data.study.storageClass === 'BEING_RESTORED'
                                ? t('Aguarde. Em até 12 horas o exame estará disponível')
                                : t('Baixar DICOM')
                          }>
                            <span style={{ margin: 'auto' }}>
                              <Button
                                variant='outlined'
                                onClick={() => handleDownloadStudy(
                                  accountId, data?.checkDownloadLink, CHECK_DOWNLOAD_INTERVAL, data?.downloadStudyLink,
                                  downloadZipUrl, data.study.patientName, setDownloadStatus, data.study.id, { type: 'paciente' }
                                )}
                                style={{
                                  background: (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE')
                                    ? '#c30000'
                                    : data.study.storageClass === 'BEING_RESTORED' && '#ffff99',
                                  border: '1px solid rgba(19, 11, 31, 0.5)',
                                  color: (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE') && 'white'
                                }}
                                disabled={!data?.downloadStudyLink
                                  || (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE'
                                    || data.study.storageClass === 'BEING_RESTORED')}
                              >
                                {
                                  (data.study.storageClass === 'GLACIER' || data.study.storageClass === 'DEEP_ARCHIVE')
                                    ? t('DICOM arquivado')
                                    : data.study.storageClass === 'BEING_RESTORED'
                                      ? t('DICOM em recuperação')
                                      : t('Baixar DICOM')
                                }
                              </Button>
                            </span>
                          </Tooltip>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
          </Paper>
          {brand === 'human' && isSmall &&
            <Box sx={{textAlign: "center", marginTop: '7.2vw'}}>
              <a href="https://drtis.com.br">
                <img src={logo} alt={'Dr. Tis'} height="42" />
              </a>
            </Box>
          }
        </Container>
      </div>

      { !isMobile && <ReportModal open={laudoOpen} url={urlLaudo} downloadUrl={downloadLaudoUrl} onClose={() => setLaudoOpen(false)} /> }
      <ViewerModal open={!!viewerUrl} viewerUrl={viewerUrl} onClose={() => setViewerUrl(undefined)} />
    </>
  );
};

export default DashboardComplete;
